import type { GetItemPropsOptions } from 'downshift';

import type { BackwardsCompatibleMutableRefObject } from '@adsk/alloy-react-helpers';

import { includesItem } from './itemKeyHelpers';
import { getSharedFooterProps } from './sharedFooterLogic';
import type {
  GetSelectedItemProps,
  FooterProps,
  GenericItem,
  GetItemProps,
  GetItemKey,
  OnItemSelect,
  SelectionAction,
} from '../types';

export const getMultipleSelectionHandlers = <TItem extends GenericItem>({
  id,
  selectedItems,
  setSelectedItems,
  isItemDisabled,
  closeMenu,
  rawGetItemProps,
  rawGetSelectedItemProps,
  refToFocusOnClear,
  getItemKey,
}: {
  id: string;
  selectedItems: TItem[];
  setSelectedItems: (items: TItem[]) => void;
  isItemDisabled: (item: TItem) => boolean;
  closeMenu: () => void;
  rawGetItemProps: GetItemProps<TItem>;
  rawGetSelectedItemProps: GetSelectedItemProps<TItem>;
  refToFocusOnClear: BackwardsCompatibleMutableRefObject<HTMLElement>;
  getItemKey?: GetItemKey<TItem>;
}) => {
  const getSelectedItemProps: GetSelectedItemProps<TItem> = (options) => {
    const disabled = isItemDisabled(options.selectedItem);
    return rawGetSelectedItemProps({ disabled, ...options });
  };

  const reset = () => {
    const disabledSelectedItems = selectedItems.filter((item) =>
      isItemDisabled(item),
    );

    setSelectedItems(disabledSelectedItems);
  };

  const footerProps = getSharedFooterProps({
    id,
    reset,
    closeMenu,
    refToFocusOnClear,
  });

  const getFooterProps: () => FooterProps = () => ({
    selectedItems,
    ...footerProps,
  });

  const getItemProps = ({
    item,
    isSelected: isSelectedProp,
    ...itemProps
  }: GetItemPropsOptions<TItem>) => {
    const isSelected =
      isSelectedProp || includesItem(selectedItems, item, getItemKey);

    const toReturn = rawGetItemProps({ item, isSelected, ...itemProps });

    return {
      isMultiple: true,
      ...toReturn,
    };
  };

  return {
    controlProps: { getSelectedItemProps },
    menuProps: {
      getFooterProps,
      getItemProps,
    },
  };
};

export const toggleItemSelection = <TItem extends GenericItem>({
  item,
  selectedItems,
  addSelectedItem,
  removeSelectedItem,
  getItemKey,
}: {
  /** The item that was just clicked or chosen via keyboard */
  item: TItem | null | undefined;
  /** The currently selected items */
  selectedItems: TItem[];
  /** Function to add an item to the selected items */
  addSelectedItem: (item: TItem) => void;
  /** Function to remove an item from the selected items */
  removeSelectedItem: (item: TItem) => void;
  /** Optional function to get unique key for an item */
  getItemKey?: GetItemKey<TItem>;
}): SelectionAction | undefined => {
  if (!item) {
    return undefined;
  }

  if (includesItem(selectedItems, item, getItemKey)) {
    removeSelectedItem(item);
    return 'deselect';
  } else {
    addSelectedItem(item);
    return 'select';
  }
};

/**
 * Toggles item selection and fires `onItemSelect` with optimistic post-change state.
 * Shared by `useMultiAutocomplete` and `useMultiDropdown`.
 */
export const handleMultiItemSelect = <TItem extends GenericItem>({
  item,
  selectedItems,
  addSelectedItem,
  removeSelectedItem,
  getItemKey,
  onItemSelect,
}: {
  item: TItem;
  selectedItems: TItem[];
  addSelectedItem: (item: TItem) => void;
  removeSelectedItem: (item: TItem) => void;
  getItemKey?: GetItemKey<TItem>;
  onItemSelect?: OnItemSelect<TItem>;
}) => {
  const action = toggleItemSelection({
    item,
    selectedItems,
    addSelectedItem,
    removeSelectedItem,
    getItemKey,
  });

  if (onItemSelect && action) {
    const postChangeItems =
      action === 'select'
        ? [...selectedItems, item]
        : selectedItems.filter((s) =>
            getItemKey ? getItemKey(s) !== getItemKey(item) : s !== item,
          );
    onItemSelect(item, {
      action,
      selectedItem: null,
      selectedItems: postChangeItems,
    });
  }
};
